import { useState, useEffect, useRef } from 'react';
import { HiOutlineX, HiOutlineDownload, HiOutlineExternalLink } from 'react-icons/hi';
import { ExportActions, QualityReport } from './AssetExtras';

/**
 * Reusable asset preview modal.
 * Props:
 * - previewUrl: URL to fetch preview content (e.g. /api/assets/:id/preview)
 * - downloadUrl: URL to download the file
 * - fileFormat: 'html', 'docx', 'pdf', 'pptx'
 * - assetLabel: display name of the asset type
 * - onClose: function to close the modal
 * - assetType, assetId (optional, partner assets): enable extra export formats
 * - qualityReport (optional): automatic quality-check results to show
 */
export default function AssetPreviewModal({ previewUrl, downloadUrl, fileFormat, assetLabel, onClose, assetType, assetId, qualityReport }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showQuality, setShowQuality] = useState(false);
  const iframeRef = useRef(null);
  const printPreview = () => iframeRef.current?.contentWindow?.print();

  const canPreview = ['html', 'docx', 'pdf'].includes(fileFormat);

  useEffect(() => {
    // For iframe-based preview, we just set loading false once iframe loads
    if (!canPreview) {
      setLoading(false);
      setError('Preview is not available for this file type. Please download to view.');
    }
  }, [canPreview]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Preview: {assetLabel}</h2>
            <span className="text-xs text-gray-400 uppercase">{fileFormat}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {qualityReport && (
              <button onClick={() => setShowQuality(v => !v)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50">
                {showQuality ? 'Hide checks' : 'Quality checks'}
              </button>
            )}
            {assetType && assetId && (
              <ExportActions assetType={assetType} assetId={assetId} previewUrl={previewUrl} onPrint={printPreview} />
            )}
            {previewUrl && canPreview && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <HiOutlineExternalLink className="w-4 h-4" />
                Open in Tab
              </a>
            )}
            {downloadUrl && (
              <a
                href={downloadUrl}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <HiOutlineDownload className="w-4 h-4" />
                Download
              </a>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <HiOutlineX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {showQuality && qualityReport && (
          <div className="px-6 py-3 border-b border-gray-200 max-h-60 overflow-auto flex-shrink-0">
            <QualityReport report={qualityReport} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden relative bg-gray-50">
          {canPreview ? (
            <>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Loading preview...</p>
                  </div>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={previewUrl}
                className="w-full h-full border-0"
                onLoad={() => setLoading(false)}
                onError={() => { setLoading(false); setError('Failed to load preview'); }}
                title="Asset Preview"
                sandbox="allow-same-origin allow-modals"
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md px-6">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-gray-400">.{fileFormat}</span>
                </div>
                <p className="text-gray-500 mb-4">{error || 'Preview is not available for this file type.'}</p>
                {downloadUrl && (
                  <a
                    href={downloadUrl}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                  >
                    <HiOutlineDownload className="w-5 h-5" />
                    Download {fileFormat?.toUpperCase()}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
